import React, { useEffect, useMemo } from "react"

import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"

import {
    AvailablePlanStatus,
    CalculatorResultType,
} from "@common-shared/config/coverage.config.ts"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { AnalyticsEvent } from "../../../config/analytics.config"
import {
    ActiveCoverageNoticeType,
    GENERAL_CONFIG,
} from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { AnalyticsHelper } from "../../../helpers/analytics.helper"
import { calculatorSelector } from "../../../selectors/calculator.selector"
import { CoverageBreakdownComponent } from "../coverageBreakdown/coverageBreakdown.component"

import exclamationImg from "@front-shared/assets/images/icons/exclamation-dark.svg"
import successImg from "@front-shared/assets/images/shapes/success.svg"

export const CalculatorSuccessComponent: React.FunctionComponent = () => {
    useScrollFix()

    const navigate = useNavigate()

    const { embedKey } = useParams()

    const calculatorState = useSelector(calculatorSelector)

    const activeCoverageNotices = useMemo<ActiveCoverageNoticeType[]>(() => {
        const result = [] as ActiveCoverageNoticeType[]

        if (!calculatorState.data) {
            return result
        }

        // NOTICE for 206
        if (
            calculatorState.data.resultType ===
            CalculatorResultType.activePartialCoverage
        ) {
            result.push(ActiveCoverageNoticeType.partialCoverage)
        }

        // NOTICE for plan status ACTIVE AT RISK
        if (
            calculatorState.data.planStatus === AvailablePlanStatus.activeAtRisk
        ) {
            result.push(ActiveCoverageNoticeType.planActiveAtRisk)
        }

        return result
    }, [calculatorState.data])

    useEffect(() => {
        if (!calculatorState.data) {
            void navigate(
                embedKey
                    ? ROUTES_CONFIG.embedCalculatorUrl.replace(
                          ":embedKey",
                          embedKey,
                      )
                    : ROUTES_CONFIG.calculatorUrl,
            )
        } else {
            AnalyticsHelper.event(AnalyticsEvent.calculatorResultEligible)
        }
    }, [])

    const getRenderedCoverageNotice = (
        noticeType: ActiveCoverageNoticeType,
    ): React.JSX.Element => {
        let badge: string | undefined = undefined
        const badgeColors = "bg-brand-vibrantPurple text-white"
        let message: string | React.JSX.Element = ""
        let isBoldMessage = true

        switch (noticeType) {
            case ActiveCoverageNoticeType.partialCoverage:
                isBoldMessage = false
                message = (
                    <>
                        This plan is Active, however Nirvana does not yet
                        support cost-estimates on this plan.
                    </>
                )

                break

            case ActiveCoverageNoticeType.planActiveAtRisk:
                isBoldMessage = false
                badge = "Active - At Risk"
                message = (
                    <>
                        This plan is Active, but is flagged by the Insurer as
                        either delinquent or under investigation and there is a
                        risk the Insurer will not provide coverage for services
                        rendered. You should reach out to your insurance company
                        and resolve any outstanding issues.
                    </>
                )

                break
        }

        return (
            <div
                className={`
                    mt-[10px] flex gap-[16px] p-[16px] rounded-[8px]
                    bg-brand-offWhite border border-solid border-brand-warmShadow
                    md:block md:mt-[0px]
                    ${badge ? "items-start" : "items-center"}
                `}
            >
                {badge ? (
                    <div
                        className={`
                            px-[10px] py-[5px] rounded-[6px] whitespace-nowrap
                            ${badgeColors}
                            md:text-center
                        `}
                    >
                        {badge}
                    </div>
                ) : (
                    <img
                        src={exclamationImg}
                        className="md:hidden"
                        alt="Attention"
                    />
                )}

                <PrimaryText
                    className={`
                        ${badge ? "md:mt-[24px]" : ""}
                        md:text-center
                    `}
                    typography={isBoldMessage ? "textBold" : "text"}
                >
                    {message}
                </PrimaryText>
            </div>
        )
    }

    return (
        <div className="w-full">
            <div className="primary-container">
                <PageHelmetElement
                    title="Success"
                    defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
                />

                <div className="mt-[16px] flex items-center sm:block">
                    <div className="mr-[24px] sm:hidden">
                        <img src={successImg} alt="Success" />
                    </div>

                    <div className="flex-1 sm:mt-[16px]">
                        <PrimaryText typography="h1">Congrats!</PrimaryText>

                        <PrimaryText className="mt-[8px]">
                            Great news{" "}
                            <span className="mouseflow-ignore">
                                {calculatorState.inputData?.firstName}
                            </span>
                            ! You are eligible for mental health benefits under
                            your current health plan.
                        </PrimaryText>
                    </div>
                </div>

                {/*Coverage notices*/}
                <div className="mt-[60px] md:mt-[24px]">
                    {activeCoverageNotices.length
                        ? activeCoverageNotices.map((item, index) => (
                              <div key={index}>
                                  {getRenderedCoverageNotice(item)}
                              </div>
                          ))
                        : null}
                </div>

                <CoverageBreakdownComponent className="mt-[40px]" />

                {/*<FAQComponent />*/}
            </div>
        </div>
    )
}
